import { HttpClient, HttpContext, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { environment } from '@environments/environment'
import { Observable } from 'rxjs'
import { CreateEventAppPayload, EventApp, UpdateEventAppPayload } from '../types/event-app.type'
import { PersonaSettingsQuery, selfPrintingPersonaSettings } from '../types/settings.type'

@Injectable()
export class SelfSettingsService {
  private readonly _http = inject(HttpClient)
  constructor() {}

  /**
   * Get the persona settings for an event. When `type` is omitted the endpoint returns
   * self-printing settings; passing `HANDSHAKE` returns handshake settings for the
   * handshake setup modal. An optional `flag` filters personas (e.g. `regular_persona`
   * for invitation tracker tabs); self-print callers should omit it.
   * @param organizationId {number} - The ID of the organization
   * @param eventId {number} - The ID of the event
   * @param query {PersonaSettingsQuery} - Optional `type` (e.g. `HANDSHAKE`), `flag` (e.g. `regular_persona`),
   * and `registerCount` (adds `register_count=true` so each row includes `registration_count`)
   * @returns {Observable<Response<selfPrintingPersonaSettings>>} - API payload in `data`
   */
  public getSelfSettings(organizationId: number, eventId: number, query: PersonaSettingsQuery = {}): Observable<Response<selfPrintingPersonaSettings>> {
    let params = new HttpParams()
    if (query.type) params = params.set('type', query.type)
    if (query.flag) params = params.set('flag', query.flag)
    if (query.registerCount) params = params.set('register_count', 'true')
    return this._http.get<Response<selfPrintingPersonaSettings>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas/settings`, {
      params,
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Update the self printing persona settings for an event
   * @param organizationId {number} - The ID of the organization
   * @param eventId {number} - The ID of the event
   * @param payload {{ settings: selfPrintingPersonaSettings }} - Body with `settings` array
   * @returns {Observable<Response<selfPrintingPersonaSettings>>} - Updated rows in `data`
   */
  public updateSelfSettings(organizationId: number, eventId: number, payload: { settings: selfPrintingPersonaSettings }): Observable<Response<selfPrintingPersonaSettings>> {
    return this._http.patch<Response<selfPrintingPersonaSettings>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/personas/settings`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Gets the event apps for an event.
   * @param organizationId {number} - The ID of the organization
   * @param eventId {number} - The ID of the event
   * @returns {Observable<Response<EventApp[]>>} - API payload in `data`
   */
  public getEventApps(organizationId: number, eventId: number): Observable<Response<EventApp[]>> {
    return this._http.get<Response<EventApp[]>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/event-apps`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Creates an event app for an event.
   * @param organizationId {number} - The ID of the organization
   * @param eventId {number} - The ID of the event
   * @param payload {CreateEventAppPayload} - The payload to create the event app.
   * @returns {Observable<Response<EventApp>>} - API payload in `data`
   */
  public createEventApp(organizationId: number, eventId: number, payload: CreateEventAppPayload): Observable<Response<EventApp>> {
    return this._http.post<Response<EventApp>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/event-apps`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Updates an event app for an event.
   * @param organizationId {number} - The ID of the organization
   * @param eventId {number} - The ID of the event
   * @param appId {number} - The ID of the event app
   * @param payload {UpdateEventAppPayload} - The payload to update the event app.
   * @returns {Observable<Response<EventApp>>} - API payload in `data`
   */
  public updateEventApp(organizationId: number, eventId: number, appId: number, payload: UpdateEventAppPayload): Observable<Response<EventApp>> {
    return this._http.put<Response<EventApp>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/event-apps/${appId}`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Deletes an event app for an event.
   * @param organizationId {number} - The ID of the organization
   * @param eventId {number} - The ID of the event
   * @param appId {number} - The ID of the event app
   * @returns {Observable<Response<{ data: [], message: string }>>} - API payload in `data`
   */
  public deleteEventApp(organizationId: number, eventId: number, appId: number): Observable<Response<{ data: []; message: string }>> {
    return this._http.delete<Response<{ data: []; message: string }>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/event-apps/${appId}`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }
}
